import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2, Printer } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import apiClient from '@/api/client'
import { formatDateTimeHuman } from '@/lib/utils'
import { printFbrCreditNote } from '@/lib/printFbrCreditNote'
import { printRefundReceipt } from '@/lib/printRefundReceipt'
import { parseReceiptSettings } from '@/lib/printCustomerReceipt'
import type { FiscalNote } from '@/types'

interface Props {
  noteId: string
  /** Optional preloaded payload — skips the fetch when provided. */
  initialNote?: FiscalNote | null
  /** Original order number — surfaced on the refund receipt when no FBR IRN exists. */
  orderNumber?: string
  onClose: () => void
}

/**
 * CreditNoteView renders a credit note (FBR-fiscalized) or refund receipt
 * (non-fiscal tenant) as a printable document. Layout mirrors other receipt
 * surfaces; switches IRN/QR rows off when there are no fiscal_details.
 */
export function CreditNoteView({ noteId, initialNote, orderNumber, onClose }: Props) {
  const [isPrinting, setIsPrinting] = useState(false)

  const noteQuery = useQuery({
    queryKey: ['fiscal-note', noteId],
    queryFn: () => apiClient.getFiscalNote(noteId),
    enabled: !initialNote,
  })

  // Receipt branding (logo, business name, contact, accent, refund footer)
  // for the customer-facing refund receipt. Cached app-wide; same shape the
  // sales receipt and stations consume.
  const settingsQuery = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    staleTime: 5 * 60 * 1000,
  })
  const receiptSettings = parseReceiptSettings(
    settingsQuery.data?.data as Record<string, unknown> | undefined,
  )

  const note: FiscalNote | undefined =
    initialNote ?? noteQuery.data?.data ?? undefined
  const isFiscal = !!note?.fiscal_details
  const heading = isFiscal ? 'Credit Note' : 'Refund'
  const totalLabel = isFiscal ? 'Total credit' : 'Total refund'

  const handlePrint = () => {
    setIsPrinting(true)
    const res = note
      ? printFbrCreditNote(note, { orderNumber })
      : { printed: false }
    // Reset quickly; the actual print happens in the popup window.
    setTimeout(() => setIsPrinting(false), res.printed ? 250 : 0)
  }

  const handlePrintRefundReceipt = () => {
    setIsPrinting(true)
    const res = note
      ? printRefundReceipt(note, receiptSettings, { orderNumber })
      : { printed: false }
    setTimeout(() => setIsPrinting(false), res.printed ? 250 : 0)
  }

  useEffect(() => {
    document.body.classList.add('credit-note-print-host')
    return () => document.body.classList.remove('credit-note-print-host')
  }, [])

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{heading}</DialogTitle>
          <DialogDescription>
            {isFiscal
              ? 'FBR-fiscalized credit note linked to the original synced invoice.'
              : 'Refund record linked to the original order. Payments are reversed automatically; no FBR submission.'}
          </DialogDescription>
        </DialogHeader>

        {!note && noteQuery.isLoading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        )}

        {note && (
          <div
            id="credit-note-printable"
            className="bg-background border rounded-md p-4 font-mono text-xs space-y-3 print:border-0 print:p-0"
          >
            <div className="text-center space-y-1">
              <p className="text-base font-bold tracking-wider">
                {isFiscal ? 'CREDIT NOTE' : 'REFUND RECEIPT'}
              </p>
              <p className="text-[10px] text-muted-foreground">
                Generated {formatDateTimeHuman(note.created_at)}
              </p>
              <p className="text-[10px]">
                Status:{' '}
                <span
                  className={
                    note.status === 'synced'
                      ? 'text-success-fg font-semibold'
                      : note.status === 'failed'
                      ? 'text-danger-fg font-semibold'
                      : 'text-warning-fg font-semibold'
                  }
                >
                  {isFiscal ? note.status.toUpperCase() : 'REFUNDED'}
                </span>
              </p>
            </div>

            <div className="border-t border-dashed pt-2 space-y-1">
              {isFiscal && note.ref_invoice_number && (
                <DetailLine label="Original Invoice IRN" value={note.ref_invoice_number} />
              )}
              {isFiscal && note.ref_usin && (
                <DetailLine label="Original USIN" value={note.ref_usin} />
              )}
              {!isFiscal && orderNumber && (
                <DetailLine label="Order #" value={orderNumber} />
              )}
              {note.fiscal_details?.irn && (
                <DetailLine label="Credit Note IRN" value={note.fiscal_details.irn} />
              )}
              {note.fiscal_details?.fiscal_usin && (
                <DetailLine label="CN USIN" value={note.fiscal_details.fiscal_usin} />
              )}
              {note.created_by_name && (
                <DetailLine label="Issued by" value={note.created_by_name} />
              )}
              {note.reason && <DetailLine label="Reason" value={note.reason} />}
            </div>

            <div className="border-t border-dashed pt-2">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                Items credited
              </p>
              <div className="space-y-1">
                {note.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="truncate">{item.product_name}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {item.quantity} × {item.unit_price.toFixed(2)}
                      </p>
                    </div>
                    <span className="text-danger-fg tabular-nums font-semibold">
                      −{item.total.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-dashed pt-2 space-y-1">
              <DetailLine label="Subtotal" value={`−${note.subtotal.toFixed(2)}`} />
              {note.tax_amount > 0 && (
                <DetailLine label="Tax" value={`−${note.tax_amount.toFixed(2)}`} />
              )}
              <DetailLine
                label={totalLabel}
                value={`−${note.total_amount.toFixed(2)}`}
                emphasis
              />
            </div>

            {note.fiscal_details?.error_log && (
              <div className="border-t border-dashed pt-2 text-[10px] text-danger-fg">
                FBR error: {note.fiscal_details.error_log}
              </div>
            )}

            <p className="text-center text-[9px] text-muted-foreground border-t border-dashed pt-2">
              This document offsets the original invoice. Both records remain
              in the audit log for compliance.
            </p>
          </div>
        )}

        <DialogFooter className="print:hidden gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handlePrint}
            disabled={!note || isPrinting}
          >
            <Printer className="w-4 h-4 mr-2" />
            {isFiscal ? 'Credit note' : 'Internal record'}
          </Button>
          <Button
            type="button"
            onClick={handlePrintRefundReceipt}
            disabled={!note || isPrinting || settingsQuery.isLoading}
          >
            <Printer className="w-4 h-4 mr-2" />
            Refund customer receipt
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DetailLine({
  label,
  value,
  emphasis,
}: {
  label: string
  value: string | number
  emphasis?: boolean
}) {
  return (
    <div
      className={`flex justify-between gap-3 ${emphasis ? 'font-semibold' : ''}`}
    >
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right break-all">{value}</span>
    </div>
  )
}
